import React, { useContext, useEffect, useMemo, useState } from 'react';

import { TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';

import StyledButton from '../../../../../shared/components/StyledButton/StyledButton';
import { TABLET_SCREEN_SIZE } from '../../../../../shared/constants/constants';
import { PHONE_PATTERN } from '../../../../../shared/constants/forms';
import { CLICK_ACTION, SAVE_CHANGES, SECTION_CONFIGURATION } from '../../../../../shared/constants/tagManager';
import { UPDATE_USER_DATA_ERROR_TOAST, UPDATE_USER_DATA_SUCCESS_TOAST } from '../../../../../shared/constants/toast';
import { SessionContext } from '../../../../../shared/context/auth/authContext';
import { useFormValidation } from '../../../../../shared/hooks/useFormValidation';
import useWindowSize from '../../../../../shared/hooks/useWindowSize/useWindowSize';
import { IPersonalData } from '../../../../../shared/interfaces/auth';
import { IToast } from '../../../../../shared/interfaces/toast';
import Users from '../../../../../shared/services/api/users/users';
import tagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_SETTINGS } from '../../../../../shared/translations/settings';
import { capitalize } from '../../../../../shared/utils/utils';

import './PersonalData.scss';

interface IPersonalDataProps {
  handleOpenToast: (toast: IToast) => void;
}

const PersonalData = ({ handleOpenToast }: IPersonalDataProps) => {
  const { user, setUserAttributes } = useContext(SessionContext);
  const mappedUser = useMemo(
    () => ({
      name: user.name,
      lastName: user.lastName,
      phone: {
        number: user.phone?.number
      }
    }),
    [user]
  );

  const [formUser, setFormUser] = useState<IPersonalData>({
    name: '',
    lastName: '',
    phone: {
      number: ''
    }
  });
  const [loading, setLoading] = useState(false);

  const formValidation = useFormValidation(
    formUser.name as string,
    formUser.lastName as string,
    formUser.phone?.number as string
  );
  const { width } = useWindowSize();
  const { t } = useTranslation();

  const disableInput = width <= TABLET_SCREEN_SIZE;

  useEffect(() => {
    setFormUser(mappedUser);
  }, [mappedUser]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormUser({ ...formUser, [e.target.id]: e.target.value });
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    !e.target.validity.patternMismatch && setFormUser({ ...formUser, phone: { number: e.target.value } });
  };

  const isDisabledSave = () => !formValidation || JSON.stringify(formUser) === JSON.stringify(mappedUser);

  const handleSaveChanges = async () => {
    tagManagerService.pushEvent(CLICK_ACTION, SAVE_CHANGES, SECTION_CONFIGURATION);
    try {
      setLoading(true);
      const userAttributes = getFieldsToUpdate(formUser, mappedUser);
      await Users.patchUserAttributes(user.email, userAttributes);
      setUserAttributes(userAttributes);
      handleOpenToast(UPDATE_USER_DATA_SUCCESS_TOAST);
    } catch (error) {
      handleOpenToast(UPDATE_USER_DATA_ERROR_TOAST);
    } finally {
      setLoading(false);
    }
  };

  const getFieldsToUpdate = (formUser: IPersonalData, mappedUser: IPersonalData) => {
    const userAttributes: any = {};
    Object.keys(formUser).forEach((key) => {
      if (key === 'phone') {
        if (formUser?.phone?.number !== mappedUser?.phone?.number) {
          userAttributes.phone = formUser?.phone;
        }
      } else if ((formUser as any)[key] !== (mappedUser as any)[key]) {
        userAttributes[key] = capitalize((formUser as any)[key].toLowerCase());
      }
    });
    return userAttributes;
  };

  return (
    <div className="personal-data-card-ne settings-account-card">
      <div className="title">{t(TRANSLATIONS_SETTINGS.personalData.title)}</div>
      <div className="personal-data-ne-form">
        <div className="personal-data-form-row">
          <TextField
            variant="standard"
            className="input"
            label={t(TRANSLATIONS_SETTINGS.personalData.name)}
            id="name"
            name="name"
            type="text"
            value={formUser?.name}
            onChange={handleChange}
            inputProps={{
              maxLength: 30,
              required: true
            }}
            disabled={disableInput}
          />
          <TextField
            variant="standard"
            className="input"
            label={t(TRANSLATIONS_SETTINGS.personalData.lastName)}
            id="lastName"
            name="lastName"
            type="text"
            value={formUser?.lastName}
            onChange={handleChange}
            inputProps={{
              maxLength: 30
            }}
            disabled={disableInput}
          />
        </div>
        <div className="personal-data-form-row">
          <TextField
            variant="standard"
            className="input"
            label={t(TRANSLATIONS_SETTINGS.personalData.email)}
            id="email"
            name="email"
            type="email"
            value={user.email}
            onChange={handleChange}
            inputProps={{
              maxLength: 150
            }}
            disabled={true}
          />
          <TextField
            variant="standard"
            className="input"
            label={t(TRANSLATIONS_SETTINGS.personalData.phone)}
            id="phone"
            name="phone"
            type="text"
            value={formUser?.phone?.number}
            onChange={handlePhoneChange}
            inputProps={{
              pattern: PHONE_PATTERN,
              maxLength: 30
            }}
            disabled={disableInput}
          />
        </div>
        <div className="personal-data-form-row right">
          <StyledButton
            buttonType="secondary"
            buttonSize="md"
            disabled={isDisabledSave()}
            action={handleSaveChanges}
            loading={loading}
            classes="save-button"
          >
            {t(TRANSLATIONS_SETTINGS.save)}
          </StyledButton>
        </div>
      </div>
    </div>
  );
};

export default PersonalData;
